// Conversation-related filters (agents, sessions, voice profiles, tools)

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createRadioFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const conversationFilters: FilterRegistry = {
	voiceKeywords: {
		...createKeywordFilter({
			label: 'Search Voices',
			placeholder: 'Search by name, description, category, accent...',
			objectTable: 'conversation_voice_profile',
			listKeys: ['conversation_voice_profile'],
			fulltextIndex: 'ft_voice_search',
			fulltextFields: ['name', 'description', 'category'],
		}),
		queryParamName: 'voiceKeywords',
	},

	agentKeywords: {
		...createKeywordFilter({
			label: 'Search Agents',
			placeholder: 'Search by name, description, type...',
			objectTable: '_conversation_agent',
			listKeys: ['conversation_agent'],
			fulltextIndex: 'ft_agent_search',
			fulltextFields: ['name', 'description', 'agent_type'],
		}),
		queryParamName: 'agentKeywords',
	},

	voiceCategory: {
		...createStatusFilter({
			label: 'Category',
			options: [
				{ id: 'premade', label: 'Pre-made' },
				{ id: 'cloned', label: 'Cloned' },
				{ id: 'generated', label: 'Generated' },
				{ id: 'professional', label: 'Professional' },
			],
			objectTable: 'conversation_voice_profile',
			fieldName: 'category',
			listKeys: ['conversation_voice_profile'],
		}),
		queryParamName: 'voiceCategory',
	},

	useCase: {
		type: 'multi-select',
		label: 'Use Case',
		placeholder: 'Select use cases...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'use_case' as keyof unknown,
			primary: 'use_case' as keyof unknown,
			count: 'voice_count' as keyof unknown,
		},
		apiEndpoint: '/ai-agents/voices/use-cases',
		queryParamName: 'useCase',
		multiple: true,
		// PD-14109: count reflects the currently filtered Voices list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'conversation_voice_profile',
			fieldName: 'use_case',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'id',
		},
		format: 'array',
		listKeys: ['conversation_voice_profile'],
	},

	gender: {
		...createRadioFilter({
			label: 'Gender',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'male', label: 'Male' },
				{ id: 'female', label: 'Female' },
				{ id: 'neutral', label: 'Neutral' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'conversation_voice_profile',
			fieldName: 'gender',
			listKeys: ['conversation_voice_profile'],
			colorScheme: 'purple',
		}),
		queryParamName: 'gender',
	},

	voiceLanguages: {
		type: 'multi-select',
		label: 'Languages',
		placeholder: 'Search languages...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'language' as keyof unknown,
			primary: 'language' as keyof unknown,
			count: 'voice_count' as keyof unknown,
		},
		apiEndpoint: '/ai-agents/voices/voice-languages',
		queryParamName: 'voiceLanguages',
		multiple: true,
		// PD-14109: count reflects the currently filtered Voices list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'conversation_voice_profile',
			fieldName: 'language',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'language',
		},
		format: 'array',
		listKeys: ['conversation_voice_profile'],
	},

	accent: {
		type: 'multi-select',
		label: 'Accent',
		placeholder: 'Search accents...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'accent' as keyof unknown,
			primary: 'accent' as keyof unknown,
			count: 'voice_count' as keyof unknown,
		},
		apiEndpoint: '/ai-agents/voices/voice-accents',
		queryParamName: 'accent',
		multiple: true,
		// PD-14109: count reflects the currently filtered Voices list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'conversation_voice_profile',
			fieldName: 'accent',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'accent',
		},
		format: 'array',
		listKeys: ['conversation_voice_profile'],
	},

	ageRange: {
		...createStatusFilter({
			label: 'Age Range',
			options: [
				{ id: 'young', label: 'Young' },
				{ id: 'middle_aged', label: 'Middle Aged' },
				{ id: 'old', label: 'Old' },
			],
			objectTable: 'conversation_voice_profile',
			fieldName: 'age_range',
			listKeys: ['conversation_voice_profile'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'ageRange',
	},

	isFavorite: {
		...createRadioFilter({
			label: 'Favorites',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'true', label: 'Favorites Only' },
				{ id: 'false', label: 'Non-Favorites' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'conversation_voice_profile',
			fieldName: 'is_favorite',
			listKeys: ['conversation_voice_profile'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'isFavorite',
	},

	agentType: {
		...createStatusFilter({
			label: 'Agent Type',
			options: [
				{ id: 'sales_assistant', label: 'Sales Assistant' },
				{ id: 'customer_service', label: 'Customer Service' },
				{ id: 'ar_collections', label: 'AR Collections' },
				{ id: 'lead_qualification', label: 'Lead Qualification' },
				{ id: 'reorder_reminder', label: 'Reorder Reminder' },
				{ id: 'order_status', label: 'Order Status' },
				{ id: 'emergency_response', label: 'Emergency Response' },
				{ id: 'custom', label: 'Custom' },
			],
			objectTable: '_conversation_agent',
			fieldName: 'agent_type',
			listKeys: ['conversation_agent'],
		}),
		queryParamName: 'agentType',
	},

	voiceProfile: {
		type: 'multi-select',
		label: 'Voice Profile',
		placeholder: 'Search voice profiles...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		searchFilterName: 'voiceSearch',
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'filterDisplay' as keyof unknown,
		},
		apiEndpoint: `/ai-agents/voices?sortFields=${encodeURIComponent(
			'[{"fieldName":"name","direction":"ASC"}]',
		)}`,
		queryParamName: 'voiceProfile',
		multiple: true,
		queryMapping: {
			objectTable: '_conversation_agent',
			fieldName: '_fk_conversation_voice_profile__id',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		format: 'array',
		listKeys: ['conversation_agent'],
	},

	agentSearch: {
		...createKeywordFilter({
			label: 'Search Agents',
			placeholder: 'Search by name, type, description...',
			objectTable: '_conversation_agent',
			listKeys: ['conversation_agent'],
			fulltextIndex: 'ft_agent_search',
			fulltextFields: [
				'name',
				'description',
				'agent_type',
				'prompt',
				'first_message',
			],
		}),
		queryParamName: 'agentSearch',
	},

	sessionSearch: {
		...createKeywordFilter({
			label: 'Search Sessions',
			placeholder: 'Search sessions...',
			objectTable: '_conversation_session',
			listKeys: ['conversation_session', 'conversation-agent-embedded'],
			// Searches session summary, agent name, phone numbers, and client name (from joined tables)
			fulltextFields: [
				'summary',
				'agent.name',
				'call.phoneFrom',
				'call.phoneTo',
				'callClient.name',
			],
		}),
		queryParamName: 'sessionSearch',
	},

	toolSearch: {
		...createKeywordFilter({
			label: 'Search Tools',
			placeholder: 'Search tools...',
			objectTable: '_conversation_tool',
			listKeys: ['conversation_tool'],
			fulltextIndex: 'fx_conversation_tool_fulltext',
			fulltextFields: ['name', 'description', 'type'],
		}),
		queryParamName: 'toolSearch',
	},

	voiceSearch: {
		...createKeywordFilter({
			label: 'Search Voices',
			placeholder: 'Search voice profiles...',
			objectTable: 'conversation_voice_profile',
			listKeys: ['conversation_voice_profile'],
			fulltextIndex: 'ft_voice_search',
			fulltextFields: [
				'name',
				'description',
				'category',
				'accent',
				'language',
				'voice_id',
			],
		}),
		queryParamName: 'voiceSearch',
	},

	agentStatus: {
		...createRadioFilter({
			label: 'Status',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
			],
			objectTable: '_conversation_agent',
			fieldName: 'isActive',
			listKeys: ['conversation_agent'],
			colorScheme: 'gray',
		}),
		queryParamName: 'agentStatus',
		loadOnFocus: true,
	},

	language: {
		type: 'single-select',
		label: 'Language',
		placeholder: 'Select language...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'language',
		multiple: false,
		options: [
			{ id: 'en', label: 'English' },
			{ id: 'es', label: 'Spanish' },
			{ id: 'fr', label: 'French' },
			{ id: 'de', label: 'German' },
			{ id: 'it', label: 'Italian' },
			{ id: 'pt', label: 'Portuguese' },
			{ id: 'pl', label: 'Polish' },
			{ id: 'tr', label: 'Turkish' },
			{ id: 'ru', label: 'Russian' },
			{ id: 'nl', label: 'Dutch' },
			{ id: 'cs', label: 'Czech' },
			{ id: 'ar', label: 'Arabic' },
			{ id: 'zh', label: 'Chinese' },
			{ id: 'ja', label: 'Japanese' },
			{ id: 'ko', label: 'Korean' },
			{ id: 'hi', label: 'Hindi' },
		],
		queryMapping: {
			objectTable: '_conversation_agent',
			fieldName: 'conversation_config',
			joinType: 'direct',
		},
		listKeys: ['conversation_agent'],
	},

	sessionDateRange: {
		...createDateRangeFilter({
			label: 'Session Date',
			objectTable: '_conversation_session',
			dateField: '_created_date',
			listKeys: ['conversation_session', 'conversation-agent-embedded'],
		}),
		queryParamName: 'sessionDateRange',
	},

	sessionAgent: {
		...createEntityLookupFilter({
			label: 'Agent',
			placeholder: 'Search agents...',
			apiEndpoint: `/ai-agents/agents?sortFields=${encodeURIComponent(
				'[{"fieldName":"name","direction":"ASC"}]',
			)}`,
			objectTable: '_conversation_session',
			fieldName: 'startingAgentId',
			listKeys: ['conversation_session'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'agent_type',
			},
			colorScheme: 'purple',
			joinType: 'direct',
		}),
		queryParamName: 'sessionAgent',
		searchFilterName: 'agentSearch',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	sessionStatus: {
		...createRadioFilter({
			label: 'Session Status',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'active', label: 'Active' },
				{ id: 'archived', label: 'Archived' },
				{ id: 'error', label: 'Error' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: '_conversation_session',
			fieldName: 'status',
			listKeys: ['conversation_session', 'conversation-agent-embedded'],
			colorScheme: 'green',
		}),
		queryParamName: 'sessionStatus',
	},

	sessionSuccess: {
		...createRadioFilter({
			label: 'Success',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'true', label: 'Success' },
				{ id: 'false', label: 'Failed' },
				{ id: 'null', label: 'Not Determined' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: '_conversation_session',
			fieldName: 'success',
			listKeys: ['conversation_session'],
		}),
		queryParamName: 'sessionSuccess',
	},

	// Voice/chat narrowing — capability carried over from the retired
	// AI Assistant Call Log's mode dropdown (2026-07 menu consolidation).
	sessionMode: {
		...createRadioFilter({
			label: 'Mode',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'voice', label: 'Voice' },
				{ id: 'chat', label: 'Chat' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: '_conversation_session',
			fieldName: 'conversation_mode',
			listKeys: ['conversation_session', 'conversation-agent-embedded'],
			colorScheme: 'blue',
		}),
		queryParamName: 'sessionMode',
	},

	callProvider: {
		type: 'text',
		label: 'Call Type',
		placeholder: 'Search by call type...',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			primary: 'value' as keyof unknown,
		},
		queryParamName: 'callProvider',
		queryMapping: {
			objectTable: '_conversation_call',
			fieldName: 'call_provider',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'value',
		},
		listKeys: ['conversation_session'],
	},

	targetPhone: {
		type: 'text',
		label: 'Target Phone',
		placeholder: 'Search by phone number...',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			primary: 'value' as keyof unknown,
		},
		queryParamName: 'targetPhone',
		queryMapping: {
			objectTable: '_conversation_call',
			fieldName: 'target_phone',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'value',
		},
		listKeys: ['conversation_session'],
	},

	sessionBatch: {
		...createEntityLookupFilter({
			label: 'Automation Batch',
			placeholder: 'Search batches...',
			apiEndpoint: `/automation/batches?sortFields=${encodeURIComponent(
				'[{"fieldName":"createdDate","direction":"DESC"}]',
			)}`,
			objectTable: '_conversation_session',
			fieldName: 'batchId',
			listKeys: ['conversation_session'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'created_date',
			},
			colorScheme: 'blue',
			joinType: 'direct',
		}),
		queryParamName: 'sessionBatch',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	sessionOutcome: {
		...createStatusFilter({
			label: 'Outcome',
			options: [
				{ id: 'success', label: 'Success' },
				{ id: 'partial', label: 'Partial' },
				{ id: 'voicemail_left', label: 'Voicemail Left' },
				{ id: 'machine_answered', label: 'Machine Answered' },
				{ id: 'no_answer', label: 'No Answer' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'callback_scheduled', label: 'Callback Scheduled' },
				{ id: 'not_interested', label: 'Not Interested' },
				{ id: 'wrong_number', label: 'Wrong Number' },
				{ id: 'busy', label: 'Busy' },
			],
			objectTable: '_conversation_session',
			fieldName: 'outcome',
			listKeys: ['conversation_session'],
			colorScheme: 'purple',
		}),
		queryParamName: 'sessionOutcome',
	},

	sessionRating: {
		...createRadioFilter({
			label: 'Rating',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'unrated', label: 'Unrated' },
				{ id: 'rated', label: 'Rated' },
				{ id: '5', label: '5 Stars' },
				{ id: '4', label: '4+ Stars' },
				{ id: '3', label: '3+ Stars' },
				{ id: '2', label: '2+ Stars' },
				{ id: '1', label: '1+ Stars' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: '_conversation_session',
			fieldName: 'rating',
			listKeys: ['conversation_session'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'sessionRating',
	},

	toolType: {
		...createStatusFilter({
			label: 'Tool Type',
			options: [
				{ id: 'system', label: 'System' },
				{ id: 'user', label: 'User' },
				{ id: 'workflow', label: 'Workflow' },
			],
			objectTable: '_conversation_tool',
			fieldName: 'type',
			listKeys: ['conversation_tool'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'toolType',
	},
};
